/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { CoreApiConfiguration } from '../core-api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { DashboardSettingsResponse as ApiDashboardSettingsResponse } from '../models/api/dashboard-settings-response';
import { FeedbackSettingsResponse as ApiFeedbackSettingsResponse } from '../models/api/feedback-settings-response';
import { LinkingImageResponse as ApiLinkingImageResponse } from '../models/api/linking-image-response';
import { ListLinkingImagesResponse as ApiListLinkingImagesResponse } from '../models/api/list-linking-images-response';
import { V3RequiredFieldsResponse as ApiV3RequiredFieldsResponse } from '../models/api/v-3-required-fields-response';
import { createFeedbackSettings } from '../fn/dashboard-settings/create-feedback-settings';
import { CreateFeedbackSettings$Params } from '../fn/dashboard-settings/create-feedback-settings';
import { createFooterLogo } from '../fn/dashboard-settings/create-footer-logo';
import { CreateFooterLogo$Params } from '../fn/dashboard-settings/create-footer-logo';
import { createV3RequiredFields } from '../fn/dashboard-settings/create-v-3-required-fields';
import { CreateV3RequiredFields$Params } from '../fn/dashboard-settings/create-v-3-required-fields';
import { deleteFeedbackSettings } from '../fn/dashboard-settings/delete-feedback-settings';
import { DeleteFeedbackSettings$Params } from '../fn/dashboard-settings/delete-feedback-settings';
import { deleteFooterLogos } from '../fn/dashboard-settings/delete-footer-logos';
import { DeleteFooterLogos$Params } from '../fn/dashboard-settings/delete-footer-logos';
import { deleteV3RequiredFields } from '../fn/dashboard-settings/delete-v-3-required-fields';
import { DeleteV3RequiredFields$Params } from '../fn/dashboard-settings/delete-v-3-required-fields';
import { getDashboardSettings } from '../fn/dashboard-settings/get-dashboard-settings';
import { GetDashboardSettings$Params } from '../fn/dashboard-settings/get-dashboard-settings';
import { getFeedbackSettings } from '../fn/dashboard-settings/get-feedback-settings';
import { GetFeedbackSettings$Params } from '../fn/dashboard-settings/get-feedback-settings';
import { getFooterLogos } from '../fn/dashboard-settings/get-footer-logos';
import { GetFooterLogos$Params } from '../fn/dashboard-settings/get-footer-logos';
import { getV3RequiredFields } from '../fn/dashboard-settings/get-v-3-required-fields';
import { GetV3RequiredFields$Params } from '../fn/dashboard-settings/get-v-3-required-fields';
import { listFooterLogos } from '../fn/dashboard-settings/list-footer-logos';
import { ListFooterLogos$Params } from '../fn/dashboard-settings/list-footer-logos';
import { updateDashboardSettings } from '../fn/dashboard-settings/update-dashboard-settings';
import { UpdateDashboardSettings$Params } from '../fn/dashboard-settings/update-dashboard-settings';
import { updateFeedbackSettings } from '../fn/dashboard-settings/update-feedback-settings';
import { UpdateFeedbackSettings$Params } from '../fn/dashboard-settings/update-feedback-settings';
import { updateFooterLogos } from '../fn/dashboard-settings/update-footer-logos';
import { UpdateFooterLogos$Params } from '../fn/dashboard-settings/update-footer-logos';
import { updateV3RequiredFields } from '../fn/dashboard-settings/update-v-3-required-fields';
import { UpdateV3RequiredFields$Params } from '../fn/dashboard-settings/update-v-3-required-fields';

@Injectable({ providedIn: 'root' })
export class DashboardSettingsService extends BaseService {
  constructor(config: CoreApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getDashboardSettings()` */
  static readonly GetDashboardSettingsPath = '/v1/boards/{boardId}/dashboard-settings';

  /**
   * Get DashboardSettings.
   *
   * Get a specific dashboardSettings
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDashboardSettings()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDashboardSettings$Response(
    params: GetDashboardSettings$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiDashboardSettingsResponse>> {
    return getDashboardSettings(this.http, this.rootUrl, params, context);
  }

  /**
   * Get DashboardSettings.
   *
   * Get a specific dashboardSettings
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDashboardSettings$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDashboardSettings(
    params: GetDashboardSettings$Params,
    context?: HttpContext
  ): Observable<ApiDashboardSettingsResponse> {
    return this.getDashboardSettings$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiDashboardSettingsResponse>): ApiDashboardSettingsResponse => r.body)
    );
  }

  /** Path part for operation `updateDashboardSettings()` */
  static readonly UpdateDashboardSettingsPath = '/v1/boards/{boardId}/dashboard-settings';

  /**
   * Update DashboardSettings.
   *
   * Update a specific DashboardSettings
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateDashboardSettings()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateDashboardSettings$Response(
    params: UpdateDashboardSettings$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return updateDashboardSettings(this.http, this.rootUrl, params, context);
  }

  /**
   * Update DashboardSettings.
   *
   * Update a specific DashboardSettings
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateDashboardSettings$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateDashboardSettings(params: UpdateDashboardSettings$Params, context?: HttpContext): Observable<string> {
    return this.updateDashboardSettings$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `getFeedbackSettings()` */
  static readonly GetFeedbackSettingsPath = '/v1/boards/{boardId}/dashboard-settings/feedback-settings';

  /**
   * Get FeedbackSettings.
   *
   * Get a specific FeedbackSettings
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getFeedbackSettings()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFeedbackSettings$Response(
    params: GetFeedbackSettings$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiFeedbackSettingsResponse>> {
    return getFeedbackSettings(this.http, this.rootUrl, params, context);
  }

  /**
   * Get FeedbackSettings.
   *
   * Get a specific FeedbackSettings
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getFeedbackSettings$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFeedbackSettings(
    params: GetFeedbackSettings$Params,
    context?: HttpContext
  ): Observable<ApiFeedbackSettingsResponse> {
    return this.getFeedbackSettings$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiFeedbackSettingsResponse>): ApiFeedbackSettingsResponse => r.body)
    );
  }

  /** Path part for operation `createFeedbackSettings()` */
  static readonly CreateFeedbackSettingsPath = '/v1/boards/{boardId}/dashboard-settings/feedback-settings';

  /**
   * Create FeedbackSettings.
   *
   * Create a FeedbackSettings
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createFeedbackSettings()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createFeedbackSettings$Response(
    params: CreateFeedbackSettings$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiFeedbackSettingsResponse>> {
    return createFeedbackSettings(this.http, this.rootUrl, params, context);
  }

  /**
   * Create FeedbackSettings.
   *
   * Create a FeedbackSettings
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createFeedbackSettings$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createFeedbackSettings(
    params: CreateFeedbackSettings$Params,
    context?: HttpContext
  ): Observable<ApiFeedbackSettingsResponse> {
    return this.createFeedbackSettings$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiFeedbackSettingsResponse>): ApiFeedbackSettingsResponse => r.body)
    );
  }

  /** Path part for operation `deleteFeedbackSettings()` */
  static readonly DeleteFeedbackSettingsPath = '/v1/boards/{boardId}/dashboard-settings/feedback-settings';

  /**
   * Delete FeedbackSettings.
   *
   * Delete an FeedbackSettings
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteFeedbackSettings()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteFeedbackSettings$Response(
    params: DeleteFeedbackSettings$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return deleteFeedbackSettings(this.http, this.rootUrl, params, context);
  }

  /**
   * Delete FeedbackSettings.
   *
   * Delete an FeedbackSettings
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteFeedbackSettings$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteFeedbackSettings(params: DeleteFeedbackSettings$Params, context?: HttpContext): Observable<string> {
    return this.deleteFeedbackSettings$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `updateFeedbackSettings()` */
  static readonly UpdateFeedbackSettingsPath = '/v1/boards/{boardId}/dashboard-settings/feedback-settings';

  /**
   * Update FeedbackSettings.
   *
   * Update a FeedbackSettings
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateFeedbackSettings()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateFeedbackSettings$Response(
    params: UpdateFeedbackSettings$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return updateFeedbackSettings(this.http, this.rootUrl, params, context);
  }

  /**
   * Update FeedbackSettings.
   *
   * Update a FeedbackSettings
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateFeedbackSettings$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateFeedbackSettings(params: UpdateFeedbackSettings$Params, context?: HttpContext): Observable<string> {
    return this.updateFeedbackSettings$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `listFooterLogos()` */
  static readonly ListFooterLogosPath = '/v1/boards/{boardId}/dashboard-settings/footer-logos';

  /**
   * List FooterLogo.
   *
   * List footerLogo
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listFooterLogos()` instead.
   *
   * This method doesn't expect any request body.
   */
  listFooterLogos$Response(
    params: ListFooterLogos$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiListLinkingImagesResponse>> {
    return listFooterLogos(this.http, this.rootUrl, params, context);
  }

  /**
   * List FooterLogo.
   *
   * List footerLogo
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listFooterLogos$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listFooterLogos(params: ListFooterLogos$Params, context?: HttpContext): Observable<ApiListLinkingImagesResponse> {
    return this.listFooterLogos$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiListLinkingImagesResponse>): ApiListLinkingImagesResponse => r.body)
    );
  }

  /** Path part for operation `createFooterLogo()` */
  static readonly CreateFooterLogoPath = '/v1/boards/{boardId}/dashboard-settings/footer-logos';

  /**
   * Create FooterLogo.
   *
   * Create a footerLogo
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createFooterLogo()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createFooterLogo$Response(
    params: CreateFooterLogo$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiLinkingImageResponse>> {
    return createFooterLogo(this.http, this.rootUrl, params, context);
  }

  /**
   * Create FooterLogo.
   *
   * Create a footerLogo
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createFooterLogo$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createFooterLogo(params: CreateFooterLogo$Params, context?: HttpContext): Observable<ApiLinkingImageResponse> {
    return this.createFooterLogo$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiLinkingImageResponse>): ApiLinkingImageResponse => r.body)
    );
  }

  /** Path part for operation `getFooterLogos()` */
  static readonly GetFooterLogosPath = '/v1/boards/{boardId}/dashboard-settings/footer-logos/{id}';

  /**
   * Get FooterLogos.
   *
   * Get a specific footerLogo
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getFooterLogos()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFooterLogos$Response(
    params: GetFooterLogos$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiLinkingImageResponse>> {
    return getFooterLogos(this.http, this.rootUrl, params, context);
  }

  /**
   * Get FooterLogos.
   *
   * Get a specific footerLogo
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getFooterLogos$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFooterLogos(params: GetFooterLogos$Params, context?: HttpContext): Observable<ApiLinkingImageResponse> {
    return this.getFooterLogos$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiLinkingImageResponse>): ApiLinkingImageResponse => r.body)
    );
  }

  /** Path part for operation `deleteFooterLogos()` */
  static readonly DeleteFooterLogosPath = '/v1/boards/{boardId}/dashboard-settings/footer-logos/{id}';

  /**
   * Delete FooterLogos.
   *
   * Delete a footerLogo
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteFooterLogos()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteFooterLogos$Response(
    params: DeleteFooterLogos$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return deleteFooterLogos(this.http, this.rootUrl, params, context);
  }

  /**
   * Delete FooterLogos.
   *
   * Delete a footerLogo
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteFooterLogos$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteFooterLogos(params: DeleteFooterLogos$Params, context?: HttpContext): Observable<string> {
    return this.deleteFooterLogos$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `updateFooterLogos()` */
  static readonly UpdateFooterLogosPath = '/v1/boards/{boardId}/dashboard-settings/footer-logos/{id}';

  /**
   * Update FooterLogos.
   *
   * Update a footerLogo
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateFooterLogos()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateFooterLogos$Response(
    params: UpdateFooterLogos$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return updateFooterLogos(this.http, this.rootUrl, params, context);
  }

  /**
   * Update FooterLogos.
   *
   * Update a footerLogo
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateFooterLogos$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateFooterLogos(params: UpdateFooterLogos$Params, context?: HttpContext): Observable<string> {
    return this.updateFooterLogos$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `getV3RequiredFields()` */
  static readonly GetV3RequiredFieldsPath = '/v1/boards/{boardId}/dashboard-settings/v3-required-settings';

  /**
   * Get V3RequiredFields.
   *
   * Get a specific V3RequiredFields
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getV3RequiredFields()` instead.
   *
   * This method doesn't expect any request body.
   *
   * @deprecated
   */
  getV3RequiredFields$Response(
    params: GetV3RequiredFields$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiV3RequiredFieldsResponse>> {
    return getV3RequiredFields(this.http, this.rootUrl, params, context);
  }

  /**
   * Get V3RequiredFields.
   *
   * Get a specific V3RequiredFields
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getV3RequiredFields$Response()` instead.
   *
   * This method doesn't expect any request body.
   *
   * @deprecated
   */
  getV3RequiredFields(
    params: GetV3RequiredFields$Params,
    context?: HttpContext
  ): Observable<ApiV3RequiredFieldsResponse> {
    return this.getV3RequiredFields$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiV3RequiredFieldsResponse>): ApiV3RequiredFieldsResponse => r.body)
    );
  }

  /** Path part for operation `createV3RequiredFields()` */
  static readonly CreateV3RequiredFieldsPath = '/v1/boards/{boardId}/dashboard-settings/v3-required-settings';

  /**
   * Create V3RequiredFields.
   *
   * Create a V3RequiredFields
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createV3RequiredFields()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   *
   * @deprecated
   */
  createV3RequiredFields$Response(
    params: CreateV3RequiredFields$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiV3RequiredFieldsResponse>> {
    return createV3RequiredFields(this.http, this.rootUrl, params, context);
  }

  /**
   * Create V3RequiredFields.
   *
   * Create a V3RequiredFields
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createV3RequiredFields$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   *
   * @deprecated
   */
  createV3RequiredFields(
    params: CreateV3RequiredFields$Params,
    context?: HttpContext
  ): Observable<ApiV3RequiredFieldsResponse> {
    return this.createV3RequiredFields$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiV3RequiredFieldsResponse>): ApiV3RequiredFieldsResponse => r.body)
    );
  }

  /** Path part for operation `deleteV3RequiredFields()` */
  static readonly DeleteV3RequiredFieldsPath = '/v1/boards/{boardId}/dashboard-settings/v3-required-settings';

  /**
   * Delete V3RequiredFields.
   *
   * Delete an V3RequiredFields
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteV3RequiredFields()` instead.
   *
   * This method doesn't expect any request body.
   *
   * @deprecated
   */
  deleteV3RequiredFields$Response(
    params: DeleteV3RequiredFields$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return deleteV3RequiredFields(this.http, this.rootUrl, params, context);
  }

  /**
   * Delete V3RequiredFields.
   *
   * Delete an V3RequiredFields
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteV3RequiredFields$Response()` instead.
   *
   * This method doesn't expect any request body.
   *
   * @deprecated
   */
  deleteV3RequiredFields(params: DeleteV3RequiredFields$Params, context?: HttpContext): Observable<string> {
    return this.deleteV3RequiredFields$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `updateV3RequiredFields()` */
  static readonly UpdateV3RequiredFieldsPath = '/v1/boards/{boardId}/dashboard-settings/v3-required-settings';

  /**
   * Update V3RequiredFields.
   *
   * Update a V3RequiredFields
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateV3RequiredFields()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   *
   * @deprecated
   */
  updateV3RequiredFields$Response(
    params: UpdateV3RequiredFields$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<string>> {
    return updateV3RequiredFields(this.http, this.rootUrl, params, context);
  }

  /**
   * Update V3RequiredFields.
   *
   * Update a V3RequiredFields
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateV3RequiredFields$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   *
   * @deprecated
   */
  updateV3RequiredFields(params: UpdateV3RequiredFields$Params, context?: HttpContext): Observable<string> {
    return this.updateV3RequiredFields$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }
}
